import { pipe, Option, Array as A } from 'effect';
import { AbilityBuilder, createMongoAbility } from '@casl/ability';
import type { RawRuleOf } from '@casl/ability';
import type { CompanySettingsAccessContext } from '@/abilities/companySettings/context';
import {
  COMPANY_SETTINGS_DESTINATION,
  type CompanySettingsAbility,
  type CompanySettingsDestinationKey,
} from '@/abilities/companySettings/vocabulary';
import { ERP_NAMES } from '@/domain/companies/helpers';

const VENDOR_BASED_APPROVALS_UNSUPPORTED_ERPS = [
  ERP_NAMES.qbo,
  ERP_NAMES.qbd,
  ERP_NAMES.sageSA,
  ERP_NAMES.sage,
  ERP_NAMES.zoho,
  ERP_NAMES.mdBC,
  ERP_NAMES.mdNavision,
  ERP_NAMES.xero,
];

// The sections are shown to whoever may change approval flows and open with the approvals feature, so the tab
// stays shown, with its stub, while the feature is off.
export const companySettingsApprovalsRules = ({
  isAdmin,
  isAccountant,
  hasApprovals,
  hasAdminOnlyApprovalFlowChanges,
  connectedErp,
}: Pick<
  CompanySettingsAccessContext,
  'isAdmin' | 'isAccountant' | 'hasApprovals' | 'hasAdminOnlyApprovalFlowChanges' | 'connectedErp'
>): RawRuleOf<CompanySettingsAbility>[] => {
  const { can, rules } = new AbilityBuilder<CompanySettingsAbility>(createMongoAbility);
  const mayChangeApprovalFlows = hasAdminOnlyApprovalFlowChanges ? isAdmin : isAccountant;
  if (mayChangeApprovalFlows) {
    const isVendorBasedApprovalsErp = !pipe(
      Option.fromNullable(connectedErp),
      Option.exists((erp) => A.contains(VENDOR_BASED_APPROVALS_UNSUPPORTED_ERPS, erp)),
    );
    const sections: CompanySettingsDestinationKey[] = [
      'approvals.approvers',
      'approvals.basicGroups',
      'approvals.advancedGroups',
      ...(isVendorBasedApprovalsErp ? (['approvals.vendorBased'] as const) : []),
      ...(isAdmin ? (['approvals.features'] as const) : []),
    ];
    can('see', COMPANY_SETTINGS_DESTINATION, { destination: { $in: sections } });
    if (hasApprovals) can('open', COMPANY_SETTINGS_DESTINATION, { destination: { $in: sections } });
  }
  return rules;
};
